import {useEffect,useRef,useState} from 'react';
import FloorEcology from './FloorEcology';
import {getSpecimenFloor,specimenTiers} from '../data/specimenCatalog';

function getFloorFromScroll(section) {
  const rect=section.getBoundingClientRect();
  const distance=Math.max(1,section.offsetHeight-window.innerHeight);
  const progress=Math.min(1,Math.max(0,-rect.top/distance));
  return Math.min(100,Math.floor(progress*99)+1);
}

export default function SpecimenCabinet({
  floor: suppliedFloor,
  embedded=false,
  lang='zh-Hant'
}) {
  const rootRef=useRef(null);
  const frameRef=useRef();
  const [scrollFloor,setScrollFloor]=useState(1);
  const [selected,setSelected]=useState(null);
  const floor=suppliedFloor || scrollFloor;
  const collection=getSpecimenFloor(floor);
  const english=lang==='en';

  useEffect(()=> {
    if (suppliedFloor) return undefined;

    const update=()=> {
      frameRef.current=undefined;
      const section=rootRef.current?.closest('.abyss-section');
      if (!section) return;
      const nextFloor=getFloorFromScroll(section);
      setScrollFloor((current)=> current===nextFloor ? current : nextFloor);
    };

    const requestUpdate=()=> {
      if (!frameRef.current) frameRef.current=requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll',requestUpdate,{passive:true});
    window.addEventListener('resize',requestUpdate);

    return ()=> {
      window.removeEventListener('scroll',requestUpdate);
      window.removeEventListener('resize',requestUpdate);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  },[suppliedFloor]);

  useEffect(()=> setSelected(null),[floor]);

  return (
    <aside
      ref={rootRef}
      className={`specimen-cabinet ${
        embedded ? 'specimen-cabinet--embedded' : ''
      }`}
      aria-live="polite"
    >
      <style>{`
        .specimen-cabinet {
          position: absolute;
          z-index: 9;
          right: 5vw;
          bottom: 5vh;
          width: min(330px,30vw);
          padding: 1.1rem 1.2rem 1.15rem;
          border: 1px solid rgba(228,190,105,.54);
          border-radius: 15px;
          color: #eee4cb;
          background:
            linear-gradient(145deg,rgba(39,31,23,.95),rgba(6,7,9,.97));
          box-shadow: 0 20px 55px #000b,inset 0 0 30px #d39d2614;
          backdrop-filter: blur(14px);
          pointer-events: auto;
        }

        .specimen-cabinet--embedded {
          position: relative;
          right: auto;
          bottom: auto;
          width: auto;
          margin-top: 1.15rem;
          padding: 1rem 0 0;
          border: 0;
          border-top: 1px solid rgba(235,221,185,.18);
          border-radius: 0;
          background: transparent;
          box-shadow: none;
          backdrop-filter: none;
        }

        .specimen-cabinet::before {
          content: "";
          position: absolute;
          top: -1px;
          left: 1.1rem;
          width: 46px;
          height: 2px;
          background: linear-gradient(90deg,#c73a2a,#f3d376);
          box-shadow: 0 0 12px #d4b26a88;
        }

        .specimen-cabinet--embedded::before {
          left: 0;
        }

        .specimen-cabinet__head {
          display: grid;
          gap: .38rem;
          margin-bottom: .75rem;
          padding-bottom: .72rem;
          border-bottom: 1px solid rgba(235,221,185,.18);
        }

        .specimen-cabinet__eyebrow {
          color: #d1ae5e;
          font: 500 .53rem "Cormorant Garamond";
          letter-spacing: .22em;
        }

        .specimen-cabinet__title {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: .5rem;
          color: #f1e2bf;
          font: 500 .9rem "Noto Serif JP";
        }

        .specimen-cabinet__title small {
          color: #a99c84;
          font: 500 .5rem "Cormorant Garamond";
          letter-spacing: .14em;
        }

        .specimen-cabinet__grid {
          display: grid;
          gap: .4rem;
          margin-top: .85rem;
        }

        .specimen-cabinet__item {
          position: relative;
          display: flex;
          align-items: center;
          gap: .56rem;
          width: 100%;
          min-height: 42px;
          padding: .43rem .42rem;
          overflow: hidden;
          border: 1px solid color-mix(
            in srgb,
            var(--tier-color),
            transparent 67%
          );
          color: #d8d0c0;
          background: rgba(5,5,5,.48);
          text-align: left;
          transition:
            transform .25s ease,
            border-color .25s ease,
            background .25s ease;
        }

        .specimen-cabinet__item.is-hidden {
          cursor: pointer;
        }

        .specimen-cabinet__item.is-hidden:hover,
        .specimen-cabinet__item.is-selected {
          border-color: var(--tier-color);
          background: rgba(53,43,29,.88);
          transform: translateX(-4px);
        }

        .specimen-cabinet__item.is-common {
          cursor: default;
          opacity: .72;
        }

        .specimen-cabinet__seal {
          position: relative;
          flex: 0 0 21px;
          width: 21px;
          height: 21px;
          border: 1px solid var(--tier-color);
          border-radius: 50%;
          background: var(--tier-color);
          box-shadow: var(--tier-glow);
        }

        .specimen-cabinet__item.is-hidden
        .specimen-cabinet__seal::after {
          content: "";
          position: absolute;
          inset: 4px;
          border: 1px solid rgba(255,255,255,.55);
          border-radius: inherit;
        }

        .specimen-cabinet__item.is-selected[data-tier="4"]::after,
        .specimen-cabinet__item.is-selected[data-tier="5"]::after,
        .specimen-cabinet__item.is-selected[data-tier="6"]::after {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          background-image:
            radial-gradient(circle,rgba(255,222,138,.7) 0 1px,transparent 2px);
          background-size: 19px 19px;
          opacity: .34;
          animation: specimen-gold-dust 1.4s linear infinite;
        }

        .specimen-cabinet__item.is-selected[data-tier="6"] {
          background:
            linear-gradient(120deg,#0a0a0a,#241631,#0c2534,#0a0a0a);
          background-size: 260% 100%;
          animation: specimen-iridescence 2.2s linear infinite;
        }

        .specimen-cabinet__item strong {
          display: block;
          overflow: hidden;
          color: #e7ddca;
          font: 500 .64rem "Noto Serif JP";
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .specimen-cabinet__item small {
          display: block;
          margin-top: .1rem;
          color: var(--tier-color);
          font: 500 .43rem "Cormorant Garamond";
          letter-spacing: .12em;
        }

        .specimen-cabinet__detail {
          min-height: 32px;
          margin-top: .62rem;
          padding: .52rem .65rem;
          border-left: 1px solid #d4b26a;
          color: #baad98;
          background: rgba(8,7,5,.55);
          font: .54rem/1.55 "Noto Serif JP";
        }

        @keyframes specimen-gold-dust {
          from {background-position: 0 0;}
          to {background-position: 19px -38px;}
        }

        @keyframes specimen-iridescence {
          to {background-position: 260% 0;}
        }

        @media (max-width: 800px) {
          .specimen-cabinet--embedded {
            margin-top: .9rem;
            padding-top: .85rem;
          }

          .specimen-cabinet__grid {
            grid-template-columns: 1fr;
          }

          .specimen-cabinet__item {
            min-width: 0;
          }

          .specimen-cabinet__item strong {
            font-size: .53rem;
          }

          .specimen-cabinet__item small {
            display: none;
          }

          .specimen-cabinet__detail {
            font-size: .49rem;
          }
        }
      `}</style>

      <div className="specimen-cabinet__head">
        <span className="specimen-cabinet__eyebrow">
          FLOOR ARCHIVE · SPECIMENS
        </span>
        <div className="specimen-cabinet__title">
          <span>
            {String(floor).padStart(3,'0')}F · {
              english ? collection.name : collection.name
            }
          </span>
          <small>{collection.zone}</small>
        </div>
      </div>

      <FloorEcology floor={floor} lang={lang} />

      <div className="specimen-cabinet__grid">
        {collection.items.map((item)=> {
          const tier=specimenTiers[item.tier];
          const isHidden=item.tier>=3;
          const glow=item.tier>=6
            ? '0 0 12px #e2b8ff,0 0 22px #7fd5ff'
            : item.tier>=5
              ? '0 0 12px #c73a2a,inset 0 0 7px #ffd700'
              : item.tier>=4
                ? '0 0 10px #d4b26a'
                : item.tier>=3
                  ? '0 0 8px #d1d5d6'
                  : item.tier===2
                    ? '0 0 5px #8ba0a866'
                    : 'none';

          return (
            <button
              className={`specimen-cabinet__item ${
                isHidden ? 'is-hidden' : 'is-common'
              } ${selected?.name===item.name ? 'is-selected' : ''}`}
              data-tier={item.tier}
              disabled={!isHidden}
              key={item.name}
              type="button"
              style={{
                '--tier-color':tier.color,
                '--tier-glow':glow
              }}
              onClick={()=> isHidden && setSelected(item)}
            >
              <i className="specimen-cabinet__seal" />
              <span>
                <strong>{item.name}</strong>
                <small>
                  {tier.name}
                  {isHidden ? ' · CLICK TO EXAMINE' : ' · CONSTANT'}
                </small>
              </span>
            </button>
          );
        })}
      </div>

      <p className="specimen-cabinet__detail">
        {selected
          ? `${selected.name} · ${selected.note || (
            english
              ? 'An unnamed memory sealed within this floor.'
              : '封存於此層的無名記憶。'
          )}`
          : english
            ? 'Common specimens remain visible. Select a rare specimen to examine its archived memory.'
            : '土與錆為常駐標本。月白以上的隱藏標本可點擊查看。'}
      </p>
    </aside>
  );
}